package com.gastoncantera.intercom.ui

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.gastoncantera.intercom.getPlatform
import com.gastoncantera.intercom.presence.PresenceRepository
import com.gastoncantera.intercom.push.PushTokenRegistry
import com.gastoncantera.intercom.push.currentPushToken

/** Indica que hay que mostrar el cartel de "activá notificaciones". [onEnable] es null en web. */
data class NotificationPrompt(val onEnable: (() -> Unit)?)

/**
 * Lógica de notificaciones para el rol usuario: pide el permiso una vez, obtiene/persiste el token
 * FCM y, si está denegado, devuelve un [NotificationPrompt] para mostrar el cartel. Devuelve `null`
 * cuando el permiso está concedido (o todavía resolviéndose): en ese caso no se muestra nada.
 *
 * Se llama SOLO desde la pantalla del usuario; el portero nunca pide este permiso.
 */
@Composable
fun rememberNotificationSetup(
    uid: String,
    presenceRepository: PresenceRepository,
): NotificationPrompt? {
    var granted by remember { mutableStateOf<Boolean?>(null) }
    var attempt by remember { mutableIntStateOf(0) }
    // Guardamos la plataforma de forma explícita ("id|descripción") para que el backend no tenga que
    // adivinarla a partir del texto libre del dispositivo (ver functions/src/notify.ts).
    val device = remember { getPlatform().let { "${it.id}|${it.name}" } }

    // Se llama incondicionalmente (registra el launcher/observer de la plataforma de forma estable).
    val action = rememberNotificationPermissionAction(onResolved = { attempt++ })

    LaunchedEffect(uid, attempt) {
        granted = ensureNotificationPermission()
        if (granted == true) currentPushToken() // dispara el fetch; publica en PushTokenRegistry
    }

    // Persiste el token actual y sus refrescos (con la descripción del dispositivo) mientras el
    // usuario esté logueado.
    LaunchedEffect(uid) {
        PushTokenRegistry.token.collect { token ->
            if (token != null) presenceRepository.addPushToken(uid, token, device)
        }
    }

    return if (granted == false) NotificationPrompt(onEnable = action) else null
}

/** Cartel no bloqueante que invita a activar las notificaciones de llamadas. */
@Composable
fun NotificationPermissionBanner(
    prompt: NotificationPrompt,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Text(
            "Activá las notificaciones para enterarte de las llamadas aunque la app esté cerrada.",
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            style = MaterialTheme.typography.bodySmall,
            textAlign = TextAlign.Center,
        )
        prompt.onEnable?.let { enable ->
            TextButton(onClick = enable, modifier = Modifier.padding(top = 2.dp)) {
                Text("Activar notificaciones")
            }
        }
    }
}

/** Cartel opcional (Android 14+) para habilitar las notificaciones estilo llamada a pantalla completa. */
@Composable
fun FullScreenCallBanner(
    onEnable: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Text(
            "Permití las notificaciones de pantalla completa para que la llamada suene y se muestre " +
                "en pantalla completa aunque el teléfono esté bloqueado.",
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            style = MaterialTheme.typography.bodySmall,
            textAlign = TextAlign.Center,
        )
        TextButton(onClick = onEnable, modifier = Modifier.padding(top = 2.dp)) {
            Text("Permitir pantalla completa")
        }
    }
}
